'use client';

import { useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import AddToCartButton from '@/components/AddToCartButton';
import { DEFAULT_SNACKBAR_DURATION } from '@/constants/static';
import { useDebounce } from '@/hooks/useDebounce';
import ProductDetailsDictionary from '@/types/dictionaries/ProductDetails';
import ProductListingDictionary from '@/types/dictionaries/ProductListing';
import { Price } from '@/types/Price';
import { LineItem } from '@/types/gearApi/Cart';
import { useToast } from '@/utils/providers/ToastProvider';
import { Analytics } from '@/utils/services/analytics';
import { addCartItem } from '../FeatureCart/actions';

interface FeatureAddToCartCtaProps {
	name: string;
	price?: Price;
	sku: string;
	unitOfMeasure?: string;
	fullWidth?: boolean;
	toastLinkUrl: string;
	sharedCartId: string | null;
	dict: ProductDetailsDictionary | ProductListingDictionary;
	warrantyId?: string | null;
	hasMultipleVariants?: boolean;
	selectVariantBtnText?: string;
}

export default function FeatureAddToCartCta({
	name,
	price,
	sku,
	unitOfMeasure,
	fullWidth,
	toastLinkUrl,
	sharedCartId,
	dict,
	warrantyId,
	hasMultipleVariants,
	selectVariantBtnText,
}: Readonly<FeatureAddToCartCtaProps>) {
	const { data: session } = useSession();
	const { showToast } = useToast();
	const { attributeLabels, messages } = dict;
	const [pendingQuantities, setPendingQuantities] = useState(0);
	const debouncedQuantities = useDebounce(pendingQuantities);
	const [isLoading, setIsLoading] = useState(false);
	const [state, setState] = useState<'none' | 'success'>('none');
	const buttonStateToggleTimeout = useRef<NodeJS.Timeout | null>(null);

	const itemLabel = debouncedQuantities > 1 ? attributeLabels.items : attributeLabels.item;

	const analyticsService = new Analytics({});

	useEffect(() => {
		const updateCartQuantities = async () => {
			setIsLoading(true);
			try {
				const lineItem: LineItem = {
					quantity: debouncedQuantities,
					sku,
					unitOfMeasure,
					warrantyId,
				};
				const { data, success } = await addCartItem(lineItem);

				if (!success || !data) {
					showToast({
						message: messages.error,
						severity: 'error',
					});
					return;
				}

				const addedItem = data.cart.lineItems.find((item) => item.sku === sku);

				if (addedItem) {
					setPendingQuantities(0);
					showToast({
						message: `${debouncedQuantities} ${itemLabel} ${messages.addToCartSuccess}`,
						linkText: attributeLabels.toastLinkText,
						linkUrl: toastLinkUrl,
						severity: 'success',
					});
					setState('success');

					buttonStateToggleTimeout.current = setTimeout(() => {
						setState('none');
					}, DEFAULT_SNACKBAR_DURATION);
				}
			} finally {
				setIsLoading(false);
			}
		};

		if (debouncedQuantities > 0) {
			void updateCartQuantities();

			analyticsService.trackAddToCart({
				sku,
				name,
				price,
				quantity: debouncedQuantities,
				isLoggedIn: !!session?.access_token,
				sharedCartId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedQuantities, warrantyId]);

	useEffect(() => {
		return () => {
			if (buttonStateToggleTimeout.current) clearTimeout(buttonStateToggleTimeout.current);
		};
	}, []);

	const handleAddToCartClick = () => {
		if (isLoading) return;

		setState('none');
		setPendingQuantities((prev) => prev + 1);
	};

	return (
		<AddToCartButton
			isLoading={isLoading}
			state={state}
			onClick={handleAddToCartClick}
			addToCartLabel={attributeLabels.addToCart}
			fullWidth={fullWidth}
			successLabel={attributeLabels.addToCartButtonSuccess}
			hasMultipleVariants={hasMultipleVariants}
			selectVariantBtnText={selectVariantBtnText}
		/>
	);
}
